import { ColumnDef } from '@/components/operations/types';
import { WorkOrderRow } from './types';

const yn = (v: boolean): string => (v ? 'Yes' : 'No');
const ynSort = (v: boolean): number => (v ? 1 : 0);
const numOrZero = (v: string): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

function textConfig<K extends keyof WorkOrderRow['config']>(
  id: string,
  label: string,
  key: K
): ColumnDef<WorkOrderRow> {
  return {
    id,
    label,
    accessor: (r) => String(r.config[key] ?? ''),
    sortValue: (r) => String(r.config[key] ?? ''),
    totalType: 'unique',
    category: 'config',
    dataType: 'enum',
    defaultHidden: true,
  };
}

function numConfig<K extends keyof WorkOrderRow['config']>(
  id: string,
  label: string,
  key: K,
  suffix = ''
): ColumnDef<WorkOrderRow> {
  return {
    id,
    label,
    accessor: (r) => {
      const v = r.config[key];
      return v ? `${v}${suffix}` : '';
    },
    sortValue: (r) => numOrZero(String(r.config[key] ?? '')),
    numericValue: (r) => numOrZero(String(r.config[key] ?? '')),
    totalType: 'sum',
    category: 'config',
    dataType: 'number',
    defaultHidden: true,
  };
}

function boolConfig<K extends keyof WorkOrderRow['config']>(
  id: string,
  label: string,
  key: K
): ColumnDef<WorkOrderRow> {
  return {
    id,
    label,
    accessor: (r) => yn(!!r.config[key]),
    sortValue: (r) => ynSort(!!r.config[key]),
    totalType: 'count',
    category: 'config',
    dataType: 'enum',
    defaultHidden: true,
  };
}

export const WORK_ORDER_COLUMNS: ColumnDef<WorkOrderRow>[] = [
  {
    id: 'status',
    label: 'Status',
    accessor: (r) => r.status,
    sortValue: (r) => r.status,
    totalType: 'unique',
    category: 'basic',
    dataType: 'enum',
    defaultFilter: ['Open', 'Ready'],
  },
  {
    id: 'builtAt',
    label: 'Built',
    accessor: (r) => r.builtAt,
    sortValue: (r) => r.builtTs,
    numericValue: (r) => r.builtTs,
    totalType: 'count',
    category: 'basic',
    dataType: 'date',
    defaultHidden: true,
  },
  {
    id: 'completedAt',
    label: 'Completed',
    accessor: (r) => r.completedAt,
    sortValue: (r) => r.completedTs,
    numericValue: (r) => r.completedTs,
    totalType: 'count',
    category: 'basic',
    dataType: 'date',
    defaultHidden: true,
  },
  {
    id: 'workOrderId',
    label: 'Work Order',
    accessor: (r) => r.workOrderId,
    sortValue: (r) => r.workOrderId,
    totalType: 'count',
    category: 'basic',
    dataType: 'string',
  },
  {
    id: 'orderNumber',
    label: 'Sales Order',
    accessor: (r) => r.orderNumber,
    sortValue: (r) => r.orderNumber,
    totalType: 'unique',
    category: 'basic',
    dataType: 'string',
  },
  {
    id: 'customer',
    label: 'Customer',
    accessor: (r) => r.customer,
    sortValue: (r) => r.customer,
    totalType: 'unique',
    category: 'basic',
    dataType: 'enum',
  },
  {
    id: 'fabricType',
    label: 'Fabric',
    accessor: (r) => r.fabricType,
    sortValue: (r) => r.fabricType,
    totalType: 'unique',
    category: 'basic',
    dataType: 'enum',
  },
  {
    id: 'frameColor',
    label: 'Frame Color',
    accessor: (r) => r.frameColor,
    sortValue: (r) => r.frameColor,
    totalType: 'unique',
    category: 'basic',
    dataType: 'enum',
  },
  {
    id: 'location',
    label: 'Location',
    accessor: (r) => r.location,
    sortValue: (r) => r.location,
    totalType: 'unique',
    category: 'basic',
    dataType: 'enum',
  },
  {
    id: 'date',
    label: 'Date',
    accessor: (r) => r.date,
    sortValue: (r) => r.dateTs,
    totalType: 'count',
    category: 'basic',
    dataType: 'date',
    numericValue: (r) => r.dateTs,
  },
  {
    id: 'opsTotal',
    label: 'Operations',
    accessor: (r) => String(r.opsTotal),
    sortValue: (r) => r.opsTotal,
    totalType: 'sum',
    numericValue: (r) => r.opsTotal,
    category: 'related',
    dataType: 'number',
  },
  {
    id: 'opsComplete',
    label: 'Ops Complete',
    accessor: (r) => String(r.opsComplete),
    sortValue: (r) => r.opsComplete,
    totalType: 'sum',
    numericValue: (r) => r.opsComplete,
    category: 'related',
    dataType: 'number',
  },
  {
    id: 'opsPercent',
    label: '% Complete',
    accessor: (r) => `${r.opsPercent}%`,
    sortValue: (r) => r.opsPercent,
    totalType: 'count',
    category: 'related',
    dataType: 'number',
    numericValue: (r) => r.opsPercent,
  },
  {
    id: 'nextStage',
    label: 'Next Operation',
    accessor: (r) => r.nextStage,
    sortValue: (r) => r.nextStage,
    totalType: 'unique',
    category: 'related',
    dataType: 'enum',
  },
  {
    id: 'kittingComplete',
    label: 'Kitting Complete',
    accessor: (r) => (r.isTKS0026 ? '—' : r.kittingComplete ? 'Yes' : 'No'),
    sortValue: (r) => (r.isTKS0026 ? -1 : r.kittingComplete ? 1 : 0),
    totalType: 'count',
    category: 'related',
    dataType: 'enum',
    defaultHidden: true,
  },
  {
    id: 'fabricComplete',
    label: 'Fabric Complete',
    accessor: (r) => (r.isTKS0026 ? '—' : r.fabricComplete ? 'Yes' : 'No'),
    sortValue: (r) => (r.isTKS0026 ? -1 : r.fabricComplete ? 1 : 0),
    totalType: 'count',
    category: 'related',
    dataType: 'enum',
    defaultHidden: true,
  },
  {
    id: 'metalComplete',
    label: 'Metal Complete',
    accessor: (r) => (r.metalComplete ? 'Yes' : 'No'),
    sortValue: (r) => (r.metalComplete ? 1 : 0),
    totalType: 'count',
    category: 'related',
    dataType: 'enum',
    defaultHidden: true,
  },
  {
    id: 'finalAssemblyComplete',
    label: 'Assembly Complete',
    accessor: (r) => (r.finalAssemblyComplete ? 'Yes' : 'No'),
    sortValue: (r) => (r.finalAssemblyComplete ? 1 : 0),
    totalType: 'count',
    category: 'related',
    dataType: 'enum',
    defaultHidden: true,
  },

  // -- Screen Config columns (hidden by default; toggle in Display Options gear) --
  textConfig('cfg_unitName', 'Unit Name', 'unitName'),
  numConfig('cfg_width', 'Width', 'width', '"'),
  numConfig('cfg_height', 'Height', 'height', '"'),

  boolConfig('cfg_completeUnit', 'Complete Unit', 'completeUnit'),
  boolConfig('cfg_buildoutIncluded', 'Buildout Included', 'buildoutIncluded'),
  boolConfig('cfg_includesHousing', 'Includes Housing', 'includesHousing'),
  boolConfig('cfg_includesSideTracks', 'Includes Side Tracks', 'includesSideTracks'),
  boolConfig('cfg_includesHemBar', 'Includes Hem Bar', 'includesHemBar'),
  boolConfig('cfg_includesFabric', 'Includes Fabric', 'includesFabric'),
  boolConfig('cfg_includesMotorTube', 'Includes Motor Tube', 'includesMotorTube'),

  textConfig('cfg_housingType', 'Housing Type', 'housingType'),
  textConfig('cfg_housingSize', 'Housing Size', 'housingSize'),

  textConfig('cfg_motorType', 'Motor Type', 'motorType'),
  textConfig('cfg_driveSide', 'Drive Side', 'driveSide'),
  textConfig('cfg_powerCord', 'Power Cord', 'powerCord'),

  textConfig('cfg_seam', 'Seam', 'seam'),
  textConfig('cfg_fabricHeightAdj', 'Fabric Height Adj', 'fabricHeightAdjustment'),
  textConfig('cfg_rotateFabric', 'Rotate Fabric', 'rotateFabric'),
  textConfig('cfg_zipperColor', 'Zipper Color', 'zipperColor'),

  textConfig('cfg_hemBarType', 'Hem Bar Type', 'hemBarType'),
  textConfig('cfg_hemBarCapColor', 'Hem Bar Cap Color', 'hemBarCapColor'),
  textConfig('cfg_pileBrush', 'Pile Brush', 'pileBrush'),

  textConfig('cfg_retention', 'Retention', 'retention'),
  textConfig('cfg_retentionLeft', 'Retention Left', 'retentionLeft'),
  textConfig('cfg_retentionRight', 'Retention Right', 'retentionRight'),
  textConfig('cfg_retentionCapColor', 'Retention Cap Color', 'retentionCapColor'),
  boolConfig('cfg_cutTracks', 'Cut Tracks', 'cutTracks'),

  boolConfig('cfg_frameColorVaries', 'Frame Color Varies', 'frameColorVaries'),
  textConfig('cfg_endCapFrameColor', 'End Cap Frame Color', 'endCapFrameColor'),
  textConfig('cfg_housingFrameColor', 'Housing Frame Color', 'housingFrameColor'),
  textConfig('cfg_hemBarFrameColor', 'Hem Bar Frame Color', 'hemBarFrameColor'),
  textConfig('cfg_retentionFrameColor', 'Retention Frame Color', 'retentionFrameColor'),
  textConfig('cfg_buildoutFrameColor', 'Buildout Frame Color', 'buildoutFrameColor'),

  textConfig('cfg_buildoutMaterial', 'Buildout Material', 'buildoutMaterial'),
  numConfig('cfg_buildoutQuantity', 'Buildout Qty', 'buildoutQuantity'),
  numConfig('cfg_buildoutLength', 'Buildout Length', 'buildoutLength'),

  boolConfig('cfg_cfTube', 'CF Tube', 'cfTube'),
  boolConfig('cfg_preDrilledTracks', 'Pre-Drilled Tracks', 'preDrilledTracks'),
  boolConfig('cfg_clearWindow', 'Clear Window', 'clearWindow'),
  numConfig('cfg_clearWindowAmount', 'Clear Window Amount', 'clearWindowAmount'),
  textConfig('cfg_clearWindowUnit', 'Clear Window Unit', 'clearWindowUnit'),

  textConfig('cfg_schedulePriority', 'Schedule Priority', 'schedulePriority'),
  textConfig('cfg_scheduleFrame', 'Schedule Frame', 'scheduleFrame'),
  textConfig('cfg_scheduleFabric', 'Schedule Fabric', 'scheduleFabric'),

  textConfig('cfg_woStatus', 'WO Status', 'woStatus'),
  boolConfig('cfg_isBuilt', 'Is Built', 'isBuilt'),
];
